import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FilePdf, WhatsappLogo, CheckCircle, Package, CalendarBlank, ShoppingCart, ClockCounterClockwise } from "@phosphor-icons/react";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import ExportSelectorDialog from "@/components/ExportSelectorDialog";
import ShoppingListBuilderDialog from "@/components/ShoppingListBuilderDialog";
import { formatCurrency } from "@/pages/sheetUtils";

export default function SavedListViewDialog(props) {
  const { t } = useLanguage();
  const { formatCurrency, generateSavedListPDF, handleConfirmSavedList, setViewDialogOpen, viewDialogOpen, viewingList } = props;
  return (
    <>
      {/* View Saved List Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ShoppingCart size={22} />
              {viewingList?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Detalhes da lista de compras salva</DialogDescription>
          </DialogHeader>
          {viewingList && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <span>{new Date(viewingList.created_at).toLocaleDateString("pt-BR")}</span>
                {viewingList.purchase_confirmed ? (
                  <span className="text-xs font-semibold bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded">Compra Confirmada</span>
                ) : (
                  <span className="text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">Pendente</span>
                )}
              </div>

              {(viewingList.sheets_used || []).length > 0 && (
                <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <p className="text-xs font-semibold uppercase text-zinc-600 mb-1">Fichas Técnicas</p>
                  {viewingList.sheets_used.map((s, i) => (
                    <p key={`sh-${i}`} className="text-sm text-zinc-700">{s.sheet_name} x{s.quantity}</p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-lg md:text-2xl font-bold text-zinc-900">{(viewingList.items || []).length}</p>
                  <p className="text-xs text-zinc-500">Ingredientes</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-lg md:text-2xl font-bold text-zinc-900">{formatCurrency(viewingList.total_cost)}</p>
                  <p className="text-xs text-zinc-500">Custo Total</p>
                </div>
              </div>

              <div className="border border-zinc-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs font-semibold">Ingrediente</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Quantidade</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Pacotes</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Custo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewingList.items || []).map((item, idx) => (
                      <TableRow key={`vi-${idx}`}>
                        <TableCell className="text-sm font-medium">{item.ingredient_name}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{item.total_quantity_needed} {item.unit}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{item.packages_needed}</TableCell>
                        <TableCell className="text-sm text-right font-mono font-bold">{formatCurrency(item.total_cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                <Button variant="outline" size="sm" onClick={() => generateSavedListPDF(viewingList)}>
                  <FilePdf size={16} className="mr-1" /> PDF
                </Button>
                {!viewingList.purchase_confirmed && (
                  <Button
                    size="sm" className="bg-zinc-900 text-white hover:bg-zinc-800"
                    onClick={() => handleConfirmSavedList(viewingList.id)}
                    data-testid="confirm-viewed-list-btn"
                  >
                    <CheckCircle size={16} className="mr-1" /> {t("sl_confirm_purchase")}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
